import { useEffect, useState, type ReactNode } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import ConfirmModal from '../Common/ConfirmModal';
import { ModalBody, ModalHeader, ModalOverlay, ModalPanel } from '../Common/ModalShell';
import Tooltip from '../Common/Tooltip';
import { supabase } from '../../lib/supabase';
import {
  SUBSCRIBER_FIELD_TYPES,
  CDN_UPLOAD_MAX_BYTES,
  isValidFieldKey,
  normalizeFieldKey,
  mapDefinitionRow,
  formatOptionsForEditor,
  parseOptions,
  SUGGESTED_ADMIN_FIELD_GROUPS,
  SUGGESTED_FIELD_TAGS,
  normalizeFieldTag,
  parseFieldTags,
  type SubscriberFieldDefinitionRow,
  type SubscriberFieldType,
  type SubscriberFieldUi,
} from '../../lib/subscriberCustomFields';

const defaultUi: SubscriberFieldUi = {
  show_on_detail: true,
  admin_only: false,
  required: false,
  sort_order: 0,
  tags: [],
};

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500';

function normalizeUiForSave(raw: SubscriberFieldUi): SubscriberFieldUi {
  const sort = Number(raw.sort_order);
  const help = raw.help_text?.trim();
  const placeholder = raw.placeholder?.trim();
  const adminGroup = raw.admin_group?.trim();
  const tags = parseFieldTags(raw.tags);
  const base: SubscriberFieldUi = {
    ...raw,
    sort_order: Number.isFinite(sort) ? Math.round(sort) : 0,
    help_text: help || undefined,
    placeholder: placeholder || undefined,
    admin_group: adminGroup || undefined,
    tags: tags.length ? tags : undefined,
  };

  if (!raw.enable_upload) {
    base.enable_upload = undefined;
    base.upload_accept = undefined;
    base.upload_max_bytes = undefined;
    base.upload_cdn_key_template = undefined;
    return base;
  }

  base.enable_upload = true;
  const accept = raw.upload_accept?.trim();
  base.upload_accept = accept || 'image/*';
  const maxMb = Number(raw.upload_max_bytes);
  if (Number.isFinite(maxMb) && maxMb > 0) {
    base.upload_max_bytes = Math.min(
      Math.round(maxMb * 1024 * 1024),
      CDN_UPLOAD_MAX_BYTES,
    );
  } else {
    base.upload_max_bytes = CDN_UPLOAD_MAX_BYTES;
  }
  const keyTemplate = raw.upload_cdn_key_template?.trim();
  base.upload_cdn_key_template = keyTemplate || 'subscribers/{subscriber_id}';
  return base;
}

export default function SubscriberFieldDefinitionModal({
  isOpen,
  onClose,
  definition,
  onSuccess,
  onDuplicated,
}: {
  isOpen: boolean;
  onClose: () => void;
  definition: SubscriberFieldDefinitionRow | null;
  onSuccess: () => void;
  onDuplicated?: (row: SubscriberFieldDefinitionRow) => void;
}) {
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<SubscriberFieldType>('text');
  const [optionsText, setOptionsText] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const [ui, setUi] = useState<SubscriberFieldUi>(defaultUi);
  const [active, setActive] = useState(true);
  const [scorePoints, setScorePoints] = useState(0);
  const [extractionPrompt, setExtractionPrompt] = useState('');
  const [saving, setSaving] = useState(false);
  const [keyEditable, setKeyEditable] = useState(false);
  const [changeKeyConfirmOpen, setChangeKeyConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setKeyEditable(false);
    setChangeKeyConfirmOpen(false);
    if (definition) {
      setKey(definition.key);
      setLabel(definition.label);
      setFieldType(definition.field_type);
      setOptionsText(formatOptionsForEditor(definition.options));
      setDefaultValue(
        definition.default_value != null ? String(definition.default_value) : '',
      );
      setUi({ ...defaultUi, ...definition.ui });
      setActive(definition.active);
      setScorePoints(definition.score_points);
      setExtractionPrompt(definition.extraction_prompt ?? '');
    } else {
      setKey('');
      setLabel('');
      setFieldType('text');
      setOptionsText('');
      setDefaultValue('');
      setUi(defaultUi);
      setActive(true);
      setScorePoints(0);
      setExtractionPrompt('');
    }
  }, [isOpen, definition]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !changeKeyConfirmOpen) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, changeKeyConfirmOpen]);

  const handleLabelBlur = () => {
    if (!definition && label.trim() && !key.trim()) {
      setKey(normalizeFieldKey(label));
    }
  };

  function buildPayload(forKey: string, forLabel: string) {
    const options = parseOptions(optionsText);
    if (fieldType === 'select' || fieldType === 'multi_select') {
      if (options.length === 0) {
        if (optionsText.trim().startsWith('[')) {
          throw new Error('Options must be a valid JSON array of strings, e.g. ["Option one", "Option two"]');
        }
        throw new Error('Select fields need at least one option');
      }
    }

    let defaultVal: unknown = null;
    if (defaultValue.trim()) {
      if (fieldType === 'boolean') {
        defaultVal = /^(true|1|yes)$/i.test(defaultValue.trim());
      } else if (fieldType === 'number') {
        const n = Number(defaultValue.trim());
        if (Number.isNaN(n)) throw new Error('Invalid default number');
        defaultVal = n;
      } else {
        defaultVal = defaultValue.trim();
      }
    }

    const points = Math.max(0, Math.min(100, Math.round(Number(scorePoints) || 0)));

    return {
      key: forKey,
      label: forLabel,
      field_type: fieldType,
      options,
      default_value: defaultVal,
      ui: normalizeUiForSave(ui),
      active,
      score_points: points,
      extraction_prompt: extractionPrompt.trim() || null,
    };
  }

  function validateKeyAndLabel(normalizedKey: string, labelText: string): string | null {
    if (!isValidFieldKey(normalizedKey)) {
      return 'Key must be 2–64 chars: lowercase letters, digits, underscores';
    }
    if (!labelText.trim()) return 'Label is required';
    return null;
  }

  async function uniqueCopyKey(baseKey: string): Promise<string> {
    const stem = baseKey.replace(/_copy\d*$/, '').replace(/_+$/, '') || 'field';
    const { data, error } = await supabase.from('subscriber_field_definitions').select('key');
    if (error) throw error;
    const existing = new Set((data ?? []).map((r) => String(r.key)));
    let candidate = `${stem}_copy`;
    let n = 2;
    while (existing.has(candidate)) {
      candidate = `${stem}_copy_${n++}`;
    }
    if (!isValidFieldKey(candidate)) {
      throw new Error('Could not generate a valid key for the copy');
    }
    return candidate;
  }

  const handleSave = async () => {
    const normalizedKey = normalizeFieldKey(key || label);
    const labelText = label.trim();
    const validation = validateKeyAndLabel(normalizedKey, labelText);
    if (validation) {
      toast.error(validation);
      return;
    }

    setSaving(true);
    try {
      const payload = buildPayload(normalizedKey, labelText);

      if (definition?.id) {
        const { error } = await supabase
          .from('subscriber_field_definitions')
          .update(payload)
          .eq('id', definition.id);
        if (error) throw error;
        toast.success('Field updated');
      } else {
        const { error } = await supabase.from('subscriber_field_definitions').insert(payload);
        if (error) throw error;
        toast.success('Field created');
      }
      onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicate = async () => {
    if (!definition?.id) return;

    const labelText = `${label.trim()} (copy)`;
    setSaving(true);
    try {
      const copyKey = await uniqueCopyKey(normalizeFieldKey(key || label));
      const validation = validateKeyAndLabel(copyKey, labelText);
      if (validation) {
        toast.error(validation);
        return;
      }

      const payload = buildPayload(copyKey, labelText);
      const { data, error } = await supabase
        .from('subscriber_field_definitions')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;

      toast.success(`Duplicated as ${copyKey}`);
      onSuccess();
      if (onDuplicated && data) {
        onDuplicated(mapDefinitionRow(data as Record<string, unknown>));
      } else {
        onClose();
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Duplicate failed');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <ModalOverlay>
      <ModalPanel maxWidthClass="max-w-3xl">
        <ModalHeader title={definition ? 'Edit field' : 'New field'} onClose={onClose} />
        <ModalBody className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Field label="Label">
              <input
                className={inputClass}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onBlur={handleLabelBlur}
                placeholder="Company size"
              />
              <FieldHint>Human-readable name on profile, subscriber detail, and exports.</FieldHint>
            </Field>
            <Field label="Key (template slug)">
              <input
                className={`${inputClass} font-mono`}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                onBlur={() => {
                  if (keyEditable || !definition?.id) {
                    setKey(normalizeFieldKey(key || label));
                  }
                }}
                placeholder="company_size"
                disabled={Boolean(definition?.id) && !keyEditable}
              />
              {definition?.id && !keyEditable ? (
                <>
                  <FieldHint>Used in templates and custom_data. Locked after creation.</FieldHint>
                  <button
                    type="button"
                    onClick={() => setChangeKeyConfirmOpen(true)}
                    className="mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:underline"
                  >
                    Change slug…
                  </button>
                </>
              ) : null}
              {definition?.id && keyEditable ? (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  Slug is unlocked. Saving renames this field in templates, API payloads, and subscriber{' '}
                  <code className="text-[11px]">custom_data</code> keys.
                </p>
              ) : (
                <FieldHint>
                  {definition?.id
                    ? 'Lowercase letters, digits, underscores only (2–64 chars).'
                    : 'Auto-filled from label if empty. Used in templates and ingest APIs as the custom_data key.'}
                </FieldHint>
              )}
            </Field>
            <Field label="Type">
              <select
                className={inputClass}
                value={fieldType}
                onChange={(e) => setFieldType(e.target.value as SubscriberFieldType)}
              >
                {SUBSCRIBER_FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <FieldHint>Controls the input widget and how values are validated and stored.</FieldHint>
            </Field>
          </div>
          {(fieldType === 'select' || fieldType === 'multi_select') && (
            <Field label="Options (JSON array)">
              <textarea
                className={`${inputClass} min-h-[220px] resize-y font-mono text-xs leading-relaxed`}
                value={optionsText}
                onChange={(e) => setOptionsText(e.target.value)}
                placeholder={'[\n  "Option one",\n  "Option two, with a comma"\n]'}
                spellCheck={false}
              />
              <FieldHint>
                One JSON array of strings. Commas inside an option are fine. On the subscribe welcome survey,
                basic HTML is rendered in buttons: <code className="text-[11px]">&lt;b&gt;</code>,{' '}
                <code className="text-[11px]">&lt;strong&gt;</code>, <code className="text-[11px]">&lt;em&gt;</code>,{' '}
                <code className="text-[11px]">&lt;u&gt;</code>, <code className="text-[11px]">&lt;span&gt;</code>,{' '}
                <code className="text-[11px]">&lt;br&gt;</code>. Saved values include the HTML. Order is display order.
              </FieldHint>
            </Field>
          )}
          {fieldType === 'url' ? (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4">
              <Toggle
                label="Enable CDN upload (engine admin)"
                tooltip="Show an upload button on connection detail. The file is stored on CDN; the public URL is saved as the field value — same as pasting a URL."
                checked={ui.enable_upload === true}
                onChange={(v) =>
                  setUi((u) => ({
                    ...u,
                    enable_upload: v,
                    upload_accept: v ? u.upload_accept || 'image/*' : undefined,
                    upload_max_bytes: v ? u.upload_max_bytes || CDN_UPLOAD_MAX_BYTES : undefined,
                    upload_cdn_key_template:
                      v ? u.upload_cdn_key_template || 'subscribers/{subscriber_id}' : undefined,
                  }))
                }
              />
              {ui.enable_upload ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Field label="Accepted file types">
                    <input
                      className={inputClass}
                      value={ui.upload_accept ?? 'image/*'}
                      onChange={(e) => setUi((u) => ({ ...u, upload_accept: e.target.value }))}
                      placeholder="image/*"
                    />
                    <FieldHint>
                      HTML accept string — e.g. <code className="text-[11px]">image/*</code> or{' '}
                      <code className="text-[11px]">image/jpeg,image/png</code>.
                    </FieldHint>
                  </Field>
                  <Field label="Max file size (MB)">
                    <input
                      type="number"
                      min={0.1}
                      max={CDN_UPLOAD_MAX_BYTES / (1024 * 1024)}
                      step={0.5}
                      className={inputClass}
                      value={
                        ui.upload_max_bytes != null
                          ? ui.upload_max_bytes / (1024 * 1024)
                          : CDN_UPLOAD_MAX_BYTES / (1024 * 1024)
                      }
                      onChange={(e) => {
                        const mb = Number(e.target.value);
                        setUi((u) => ({
                          ...u,
                          upload_max_bytes:
                            Number.isFinite(mb) && mb > 0
                              ? Math.min(Math.round(mb * 1024 * 1024), CDN_UPLOAD_MAX_BYTES)
                              : CDN_UPLOAD_MAX_BYTES,
                        }));
                      }}
                    />
                    <FieldHint>Hard cap {CDN_UPLOAD_MAX_BYTES / (1024 * 1024)} MB (CDN limit).</FieldHint>
                  </Field>
                  <Field label="CDN object key template">
                    <input
                      className={`${inputClass} font-mono text-xs`}
                      value={ui.upload_cdn_key_template ?? 'subscribers/{subscriber_id}'}
                      onChange={(e) =>
                        setUi((u) => ({ ...u, upload_cdn_key_template: e.target.value }))
                      }
                      placeholder="subscribers/{subscriber_id}"
                    />
                    <FieldHint>
                      Use <code className="text-[11px]">{`{subscriber_id}`}</code>. File extension is added
                      automatically.
                    </FieldHint>
                  </Field>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-1">
            <Toggle
              label="Show on subscriber detail"
              tooltip="Display this field in the custom fields panel on subscriber detail."
              checked={ui.show_on_detail !== false}
              onChange={(v) => setUi((u) => ({ ...u, show_on_detail: v }))}
            />
            <Toggle
              label="Admin only"
              tooltip="Restrict visibility to admin users."
              checked={ui.admin_only === true}
              onChange={(v) => setUi((u) => ({ ...u, admin_only: v }))}
            />
            <Toggle
              label="Required on ingest"
              tooltip="Mark as required when values arrive via API or webhook ingest."
              checked={ui.required === true}
              onChange={(v) => setUi((u) => ({ ...u, required: v }))}
            />
            <Toggle
              label="Active"
              tooltip="Inactive fields are hidden and excluded from profile scoring."
              checked={active}
              onChange={setActive}
            />
          </div>
          <FieldHint>
            Show on detail + not admin-only = visible on the public profile. Admin-only fields stay in the engine
            only.
          </FieldHint>
          <Field label="Admin group">
            <input
              className={inputClass}
              list="subscriber-field-admin-groups"
              value={ui.admin_group ?? ''}
              onChange={(e) => setUi((u) => ({ ...u, admin_group: e.target.value }))}
              placeholder="e.g. Subscribe onboarding"
            />
            <datalist id="subscriber-field-admin-groups">
              {SUGGESTED_ADMIN_FIELD_GROUPS.map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
            <FieldHint>
              Engine admin only — groups this field in the definitions catalog. Leave blank for{' '}
              <span className="font-medium">Ungrouped</span>.
            </FieldHint>
          </Field>
          <Field label="Tags">
            <input
              className={inputClass}
              list="subscriber-field-tags"
              value={(ui.tags ?? []).join(', ')}
              onChange={(e) =>
                setUi((u) => ({
                  ...u,
                  tags: e.target.value
                    .split(',')
                    .map((s) => normalizeFieldTag(s))
                    .filter(Boolean),
                }))
              }
              placeholder="e.g. fractional-pulse, week-3"
            />
            <datalist id="subscriber-field-tags">
              {SUGGESTED_FIELD_TAGS.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <FieldHint>
              Comma-separated. Fractional Pulse uses <span className="font-medium">fractional-pulse</span> plus{' '}
              <span className="font-medium">week-1</span>…<span className="font-medium">week-12</span>. Add{' '}
              <span className="font-medium">profile-only</span> for free-text that stays on the profile (never
              charted in the peer report).
            </FieldHint>
          </Field>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Display order">
              <input
                type="number"
                className={inputClass}
                value={ui.sort_order ?? 0}
                onChange={(e) =>
                  setUi((u) => ({ ...u, sort_order: Number(e.target.value) || 0 }))
                }
                placeholder="0"
              />
              <FieldHint>Lower numbers appear first. Use gaps (10, 20, 30) to reorder later.</FieldHint>
            </Field>
            <Field label="Placeholder">
              <input
                className={inputClass}
                value={ui.placeholder ?? ''}
                onChange={(e) => setUi((u) => ({ ...u, placeholder: e.target.value }))}
                placeholder="e.g. Acme Inc."
              />
              <FieldHint>
                Gray hint inside the empty input on profile and subscriber forms (text, number, URL, textarea
                only).
              </FieldHint>
            </Field>
            <Field label="Help hint">
              <input
                className={inputClass}
                value={ui.help_text ?? ''}
                onChange={(e) => setUi((u) => ({ ...u, help_text: e.target.value }))}
                placeholder="e.g. Approximate employee count at your company"
              />
              <FieldHint>Helper sentence shown below the control after the label.</FieldHint>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Default value (optional)">
              <input
                className={inputClass}
                value={defaultValue}
                onChange={(e) => setDefaultValue(e.target.value)}
                placeholder={defaultValuePlaceholder(fieldType)}
              />
              <FieldHint>{defaultValueHint(fieldType)}</FieldHint>
            </Field>
            <Field label="Profile score weight (0–100)">
              <input
                type="number"
                min={0}
                max={100}
                className={inputClass}
                value={scorePoints}
                onChange={(e) => setScorePoints(Number(e.target.value) || 0)}
                placeholder="0"
              />
              <FieldHint>Points added to profile completeness when this field has any value.</FieldHint>
            </Field>
          </div>

          <Field label="Transcript extraction prompt (optional)">
            <textarea
              className={`${inputClass} min-h-[88px] resize-y`}
              value={extractionPrompt}
              onChange={(e) => setExtractionPrompt(e.target.value)}
              placeholder="e.g. What is the subscriber's company size? Return null if not mentioned."
            />
            <FieldHint>
              Instructions for AI when extracting this field from a call transcript. Leave blank to skip
              auto-extraction.
            </FieldHint>
          </Field>
        </ModalBody>
        <ModalFooter
          saving={saving}
          showDuplicate={Boolean(definition?.id)}
          onClose={onClose}
          onDuplicate={() => void handleDuplicate()}
          onSave={() => void handleSave()}
        />
      </ModalPanel>
    </ModalOverlay>
    <ConfirmModal
      isOpen={changeKeyConfirmOpen}
      title="Change field slug?"
      description={
        <>
          <p className="mb-2">
            The slug <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">{definition?.key}</code>{' '}
            is used in newsletter templates, ingest APIs, and each subscriber&apos;s{' '}
            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded">custom_data</code> map.
          </p>
          <p>
            Renaming it updates stored values for every subscriber who has this field. This cannot be undone
            automatically — pick the new slug carefully.
          </p>
        </>
      }
      confirmLabel="Yes, change slug"
      variant="danger"
      onConfirm={() => {
        setKeyEditable(true);
        setChangeKeyConfirmOpen(false);
      }}
      onClose={() => setChangeKeyConfirmOpen(false)}
    />
  </>
  );
}

function ModalFooter({
  saving,
  showDuplicate,
  onClose,
  onDuplicate,
  onSave,
}: {
  saving: boolean;
  showDuplicate: boolean;
  onClose: () => void;
  onDuplicate: () => void;
  onSave: () => void;
}) {
  return (
    <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
      <div className="min-w-0">
        {showDuplicate ? (
          <button
            type="button"
            onClick={onDuplicate}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <Copy className="h-4 w-4 shrink-0" aria-hidden />
            Duplicate
          </button>
        ) : null}
      </div>
      <div className="flex shrink-0 justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      {children}
    </div>
  );
}

function FieldHint({ children }: { children: ReactNode }) {
  return <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug">{children}</p>;
}

function defaultValuePlaceholder(fieldType: SubscriberFieldType): string {
  switch (fieldType) {
    case 'boolean':
      return 'true or false';
    case 'number':
      return '42';
    case 'date':
      return '2026-05-19';
    case 'url':
      return 'https://example.com';
    case 'multi_select':
      return 'Option A, Option B';
    default:
      return 'Leave blank for no default';
  }
}

function defaultValueHint(fieldType: SubscriberFieldType): string {
  switch (fieldType) {
    case 'boolean':
      return 'Use true, false, yes, or 1/0. Applied for new subscribers when no value is set.';
    case 'number':
      return 'Must be a valid number. Applied when the field is empty on first save.';
    case 'select':
    case 'multi_select':
      return 'Must match one of the options exactly (comma-separated for multi_select).';
    default:
      return 'Pre-fills the field when empty. Does not overwrite existing subscriber values.';
  }
}

function Toggle({
  label,
  tooltip,
  checked,
  onChange,
}: {
  label: string;
  tooltip: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const control = (
    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
      <span>{label}</span>
      <span className="relative inline-flex shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <span className="block w-10 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer-checked:bg-emerald-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-4" />
      </span>
    </label>
  );

  return (
    <Tooltip label={tooltip} side="bottom" wrap>
      {control}
    </Tooltip>
  );
}
